import { FC } from "react";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { useTranslations } from "@jugl-web/utils";
import { CRMAccountCard } from "../CRMAccountCard";
import { CRMAccountTile } from "../CRMAccountTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";
import { ReactComponent as ShuffleIcon } from "./assets/shuffle.svg";
import { ReactComponent as WebChat2Icon } from "./assets/web-chat-2.svg";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useManageAccountsDrawer } from "../../providers/ManageAccountsDrawerContext";

interface WebChatBotAccountCardProps {
  variant?: "card" | "tile";
  isClickable?: boolean;
}

export const WebChatBotAccountCard: FC<WebChatBotAccountCardProps> = ({
  variant,
  isClickable,
}) => {
  const { t } = useTranslations();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const { webChatBotAccount, connectWebChatBotAccount } =
    useWebChatBotAccount();

  if (variant === "tile") {
    const handleClick = () => {
      if (!isClickable) {
        return;
      }
      if (webChatBotAccount) {
        transitionTo({ name: "webChatBotAccount" });
      } else {
        connectWebChatBotAccount().then(() =>
          openManageAccountDrawer(undefined)
        );
      }
    };

    return (
      <CRMAccountTile
        isClickable={isClickable}
        title={t({
          id: "client-support-page.web-chat",
          defaultMessage: "Web Chat",
        })}
        isConnected={!!webChatBotAccount}
        onClick={handleClick}
        icon={<WebChat2Icon />}
        // Web chat is always considered enabled for now, see WebChatBotAccountProvider
        status="available"
        features={[
          {
            icon: <DownloadIcon />,
            label: t({
              id: "client-support-page.web-chat-tile-feature-install",
              defaultMessage: "Install on your Site",
            }),
          },
          {
            icon: <UserIcon />,
            label: t({
              id: "client-support-page.web-chat-tile-feature-capture-leads",
              defaultMessage: "Capture leads",
            }),
          },
          {
            icon: <ShuffleIcon />,
            label: t({
              id: "client-support-page.web-chat-tile-feature-automated-workflows",
              defaultMessage: "Automated workflows",
            }),
          },
          {
            icon: <PenIcon />,
            label: t({
              id: "client-support-page.web-chat-tile-feature-customize-appearance",
              defaultMessage: "Customize appearance",
            }),
          },
        ]}
      />
    );
  }

  if (!webChatBotAccount) return null;

  return (
    <CRMAccountCard
      title={
        webChatBotAccount.name ||
        t({
          id: "client-support-page.web-chat",
          defaultMessage: "Web Chat",
        })
      }
      // Web chat is always considered enabled for now, see WebChatBotAccountProvider
      status="available"
      onClick={
        isClickable
          ? () => transitionTo({ name: "webChatBotAccount" })
          : undefined
      }
      // We consider the web chat bot always enabled for now
      switchProps={
        // !hideReconnectOption
        //   ? {
        //       isChecked: isConnected,
        //       onChange: toggleWebChatBotAccount,
        //       isDisabled: isToggleWebChatBotAccountLoading,
        //     }
        //   : undefined
        undefined
      }
      icon={WebChatIcon}
      description={t({
        id: "client-support-page.enhance-support-with-web-chat",
        defaultMessage:
          "Enhance customer support with integrated Web Chat on your website",
      })}
    />
  );
};
